import { useAuthStore } from '../stores/authStore.js';
import { safeRedirect } from './safeRedirect.js';
import type {
  AppliedScoreEdit,
  DifficultyLevel,
  TabEditKind,
  TabEditStatus,
  TabSong,
  TabStatus
} from '../types/index.js';

/** sessionStorage key holding the path to return to after a session-expired re-login. */
export const RETURN_PATH_KEY = 'fretboard_return_path';

export const getApiBaseUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  if (typeof window !== 'undefined' && window.location) {
    const { hostname } = window.location;
    if (
      hostname === 'fretboard.ir' ||
      hostname === 'www.fretboard.ir' ||
      hostname.endsWith('.runflare.run')
    ) {
      return 'https://api.fretboard.ir';
    }
  }
  return 'http://localhost:4000';
};

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  details?: unknown;
  code?: string;
  retryAfter?: number;
  attemptsLeft?: number;
}

export class ApiError extends Error {
  status: number;
  data: ApiErrorResponse;

  constructor(status: number, data: ApiErrorResponse) {
    super(data.message || data.error || `HTTP error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, headers, ...customConfig } = options;

  let url = `${getApiBaseUrl()}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  const token = useAuthStore.getState().token;

  const requestHeaders: Record<string, string> = {
    // FRE-151: Fastify 5 rejects Content-Type: application/json with an empty body (400), so only set it when a body is sent.
    ...(customConfig.body != null && !(customConfig.body instanceof FormData) && { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(headers as Record<string, string>)
  };

  const response = await fetch(url, {
    ...customConfig,
    headers: requestHeaders
  });

  if (!response.ok) {
    let errorData: ApiErrorResponse = {};
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    // Auto logout on 401 if token was present
    if (response.status === 401 && token) {
      const isCredentialCall = endpoint.startsWith('/api/auth/login') || endpoint.startsWith('/api/auth/register');
      try {
        if (errorData.code === 'TOKEN_REVOKED') {
          sessionStorage.setItem('fretboard_logout_reason', 'password_changed');
        } else if (!isCredentialCall) {
          // FND-09: expired session -> tell the auth page why, and remember where the user was (validated).
          sessionStorage.setItem('fretboard_logout_reason', 'session_expired');
          if (typeof window !== 'undefined' && window.location) {
            const back = safeRedirect(window.location.pathname + window.location.search, '');
            if (back) sessionStorage.setItem(RETURN_PATH_KEY, back);
          }
        }
      } catch {
        // ignore
      }
      useAuthStore.getState().logout();
    }

    // FRE-137: marketplace actions need a verified email; open the code dialog and give the caller a Persian message.
    if (response.status === 403 && errorData.code === 'EMAIL_NOT_VERIFIED') {
      useAuthStore.getState().openVerifyDialog();
      errorData = { ...errorData, message: 'برای این کار ابتدا ایمیل خود را تایید کنید.' };
    }

    throw new ApiError(response.status, errorData);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  // Handle binary responses
  const contentType = response.headers.get('content-type');
  if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('application/x-guitar-pro'))) {
    const buffer = await response.arrayBuffer();
    return buffer as unknown as T;
  }

  return response.json();
}

export const apiClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body)
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body)
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body)
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  downloadBlob: async (endpoint: string): Promise<ArrayBuffer> => {
    const token = useAuthStore.getState().token;
    const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
      ? endpoint
      : `${getApiBaseUrl()}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    const response = await fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });
    if (!response.ok) {
      throw new ApiError(response.status, { message: `Failed to download: ${response.statusText}` });
    }
    return response.arrayBuffer();
  }
};

// --- API contract types ---

/** Tab moderation status accepted by GET /api/tabs?status=. PENDING/REJECTED require auth (own tabs; admins see all). */
export type TabListStatus = 'APPROVED' | 'PENDING' | 'REJECTED';

/** Query for GET /api/tabs. `limit` is validated server-side to 1-100. */
export interface TabListParams {
  q?: string;
  tuning?: string;
  status?: TabListStatus;
  page?: number;
  limit?: number;
}

export const TAB_LIST_MAX_LIMIT = 100;

/** Max length of the admin reject reason (PATCH /api/admin/moderation/tabs/:id). */
export const REJECT_REASON_MAX_LENGTH = 300;

/** Body of PATCH /api/admin/moderation/tabs/:id. Rejecting requires a reason. */
export type AdminTabStatusUpdate = { status: 'APPROVED' } | { status: 'REJECTED'; reason: string };

/** One row of GET /api/tabs/mine (the caller's own tabs, all statuses). */
export interface MyTabStatusItem {
  id: string;
  title: string;
  artist: string;
  tuning: string;
  fileUrl: string;
  status: TabStatus;
  rejectReason: string | null;
  createdAt: string;
  rhythmDifficulty: DifficultyLevel | null;
  leadDifficulty: DifficultyLevel | null;
}

/** One row of GET /api/tabs/favorites (FRE-143). */
export interface FavoriteListItem {
  tabId: string;
  createdAt: string;
  tab: {
    id: string;
    title: string;
    artist: string;
    tuning: string;
    fileUrl: string;
    status: TabStatus;
    uploaderId: string;
    createdAt: string;
    tracks: Array<{ trackIndex: number; name: string }>;
    rhythmDifficulty: DifficultyLevel | null;
    leadDifficulty: DifficultyLevel | null;
  };
}

export interface FavoritesResponse {
  data: FavoriteListItem[];
}

/** Body of PUT /api/playlists/:id (FRE-143). */
export interface PlaylistUpsertBody {
  title: string;
  tab_ids: string[];
  created_at?: string;
}

/** Playlist as returned by GET/PUT /api/playlists (items ordered). */
export interface ApiPlaylist {
  id: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  items: Array<{
    id: string;
    playlistId: string;
    tabId: string;
    orderIndex: number;
    tab: FavoriteListItem['tab'] & { tracks?: Array<{ trackIndex: number; name: string }> };
  }>;
}

/** FRE-211: chord lyric sheet as returned by GET/PUT /api/lyrics. */
export interface ApiLyricSheet {
  id: string;
  userId: string;
  tabId: string | null;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /** PUT only: the sheet had been soft-deleted by its owner and is live again. */
  restored?: true;
}

/** FRE-211: body of PUT /api/lyrics/:id. `tab_id` omitted keeps the stored link; null unlinks. */
export interface LyricSheetUpsertBody {
  title: string;
  content: string;
  tab_id?: string | null;
  created_at?: string;
}

export const getLyricSheets = () =>
  apiClient.get<{ data: ApiLyricSheet[]; truncated: boolean }>('/api/lyrics');

export const putLyricSheet = (id: string, body: LyricSheetUpsertBody) =>
  apiClient.put<ApiLyricSheet>(`/api/lyrics/${id}`, body);

export const deleteLyricSheet = (id: string) => apiClient.delete<Record<string, never>>(`/api/lyrics/${id}`);

/** FRE-188: body of PATCH /api/tabs/:id/difficulty. Absent key = keep; null = clear (unrated). */
export interface TabDifficultyPatch {
  rhythm_difficulty?: DifficultyLevel | null;
  lead_difficulty?: DifficultyLevel | null;
}

export const patchTabDifficulty = (id: string, body: TabDifficultyPatch) =>
  apiClient.patch<TabSong & { trackCount: number; edit?: { id: string; status: 'PENDING' } }>(`/api/tabs/${id}/difficulty`, body);

export const DIFFICULTY_ERROR = {
  INVALID: 'INVALID_DIFFICULTY',
  FORBIDDEN: 'FORBIDDEN',
  NOT_EDITABLE: 'TAB_NOT_EDITABLE'
} as const;

export interface MyTabsResponse {
  data: MyTabStatusItem[];
}

/** Per-session limits enforced by POST /api/sync/sessions. */
export const SYNC_MAX_SESSIONS_PER_REQUEST = 200;
export const SYNC_MAX_CLIENT_SESSION_ID_LENGTH = 128;
export const SYNC_MAX_CATEGORY_LENGTH = 50;

export interface SyncSessionBlockPayload {
  block_id?: string;
  category?: string; // max 50 chars
  achieved_bpm: number;
}

export interface SyncSessionPayload {
  client_session_id: string; // any 1-128 char string
  total_duration_seconds: number;
  active_playback_seconds: number;
  created_at?: string;
  blocks: SyncSessionBlockPayload[];
}

export interface SyncSessionResult {
  /** null when status is 'conflict' or 'rejected' */
  id: string | null;
  client_session_id: string;
  /** 'conflict' = client_session_id belongs to another user */
  status: 'synced' | 'already_synced' | 'conflict' | 'rejected';
  /** set when status is 'rejected' */
  reason?: 'VALIDATION' | 'FUTURE_TIMESTAMP' | string;
}

export interface SyncSessionsResponse {
  message?: string;
  synced: SyncSessionResult[];
}

// --- FRE-224 Tab edits API contract (ADR 5) ---

export interface TabEditDto {
  id: string;
  tabId: string;
  kind: TabEditKind;
  status: TabEditStatus;
  payload: Record<string, unknown>;
  note: string | null;
  baseRevision: number;
  appliedRevision: number | null;
  rejectReason: string | null;
  author: { id: string; fullName: string } | null;
  legacy: boolean;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
}

export interface TabStateDto {
  id: string;
  title: string;
  artist: string;
  tuning: string;
  status: TabStatus;
  rhythmDifficulty: DifficultyLevel | null;
  leadDifficulty: DifficultyLevel | null;
  revision: number;
  updatedAt: string;
}

export interface ScoreConflict {
  index: number;
  key: string;
  code: 'NOTE_CHANGED' | 'NOTE_MISSING' | 'SLOT_OCCUPIED' | 'CHORD_CHANGED' | 'TEMPO_CHANGED';
}

export interface AdminEditItem extends TabEditDto {
  author: { id: string; fullName: string; email: string } | null;
  reviewedBy: { id: string; fullName: string } | null;
  previous: Record<string, unknown> | null;
  tab: TabStateDto & { uploaderId: string };
  stale: boolean;
  conflicts: ScoreConflict[];
}

export interface MyEditItem extends TabEditDto {
  tab: {
    id: string;
    title: string;
    artist: string;
    status: TabStatus;
    revision: number;
  } | null;
}

export interface PostTabEditBody {
  id: string;
  kind: TabEditKind;
  base_revision: number;
  note?: string | null;
  payload: Record<string, unknown>;
}

export interface PostTabEditResponse {
  edit: TabEditDto;
  tab: TabStateDto;
  applied: boolean;
  replaced: string | null;
}

export interface GetMyEditsParams {
  status?: TabEditStatus;
  tab_id?: string;
  updated_since?: string;
}

export interface TabExtrasResponse {
  tabId: string;
  revision: number;
  lyrics: {
    content: string;
    updatedAt: string;
  } | null;
  scoreEdits: AppliedScoreEdit[];
  features: {
    scoreEdits: boolean;
  };
}

export interface TabRevisionItem {
  id: string;
  revision: number;
  hasLyrics: boolean;
  updatedAt: string;
}

export interface AdminEditsParams {
  status?: TabEditStatus;
  kind?: TabEditKind;
  tab_id?: string;
  page?: number;
  limit?: number;
}

export interface AdminEditsResponse {
  data: AdminEditItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminEditDetailResponse {
  edit: AdminEditItem;
  current: {
    title: string;
    artist: string;
    tuning: string;
    rhythmDifficulty: DifficultyLevel | null;
    leadDifficulty: DifficultyLevel | null;
    lyrics: string | null;
    revision: number;
  };
  pendingSameKind: number;
}

export type PatchAdminEditBody =
  | { status: 'APPROVED' }
  | { status: 'REJECTED'; reason: string };

export interface PatchAdminEditResponse {
  edit: AdminEditItem;
  tab: TabStateDto;
}

export const TAB_EDIT_ERROR = {
  INVALID_EDIT: 'INVALID_EDIT',
  INVALID_LYRICS: 'INVALID_LYRICS',
  EDIT_NO_CHANGE: 'EDIT_NO_CHANGE',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  EDIT_ID_TAKEN: 'EDIT_ID_TAKEN',
  EDIT_NOT_PENDING: 'EDIT_NOT_PENDING',
  EDIT_CONFLICT: 'EDIT_CONFLICT',
  TAB_OPS_LIMIT: 'TAB_OPS_LIMIT',
  TOO_MANY_PENDING: 'TOO_MANY_PENDING',
  RATE_LIMITED: 'RATE_LIMITED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  INVALID_DIFFICULTY: 'INVALID_DIFFICULTY'
} as const;

export type TabEditErrorCode = typeof TAB_EDIT_ERROR[keyof typeof TAB_EDIT_ERROR];

export const postTabEdit = (tabId: string, body: PostTabEditBody) =>
  apiClient.post<PostTabEditResponse>(`/api/tabs/${tabId}/edits`, body);

export const getTabEdits = (tabId: string, params?: { status?: TabEditStatus }) =>
  apiClient.get<{ data: TabEditDto[] }>(`/api/tabs/${tabId}/edits`, {
    params: params as Record<string, string | number | boolean | undefined> | undefined
  });

export const getMyEdits = (params?: GetMyEditsParams) =>
  apiClient.get<{ data: MyEditItem[]; truncated: boolean }>('/api/tabs/mine/edits', {
    params: params as Record<string, string | number | boolean | undefined> | undefined
  });

export const withdrawTabEdit = (tabId: string, editId: string) =>
  apiClient.delete<{ edit: TabEditDto }>(`/api/tabs/${tabId}/edits/${editId}`);

export const getTabExtras = (tabId: string) =>
  apiClient.get<TabExtrasResponse>(`/api/tabs/${tabId}/extras`);

export const postTabRevisions = (ids: string[]) =>
  apiClient.post<{ data: TabRevisionItem[] }>('/api/tabs/revisions', { ids });

export const getAdminEdits = (params?: AdminEditsParams) =>
  apiClient.get<AdminEditsResponse>('/api/admin/moderation/edits', {
    params: params as Record<string, string | number | boolean | undefined> | undefined
  });

export const getAdminEdit = (editId: string) =>
  apiClient.get<AdminEditDetailResponse>(`/api/admin/moderation/edits/${editId}`);

export const patchAdminEdit = (editId: string, body: PatchAdminEditBody) =>
  apiClient.patch<PatchAdminEditResponse>(`/api/admin/moderation/edits/${editId}`, body);

